import React, { useEffect, useState } from "react";

import IntegratedDesignsFigure from "public/images/enerflo-integrated-residential-solar-pv-system-design.png";
import ReportingFigure from "public/images/enerflo-residential-solar-analytics-reporting-business-intelligence.png";
import ContractingFigure from "public/images/enerflo-residential-solar-contracting-docsigning.png";
import CustomerPortalFigure from "public/images/enerflo-residential-solar-customer-portals-install-tracker.png";
import CustomerSupportFigure from "public/images/enerflo-residential-solar-customer-support.png";
import FinanceIntegrationsFigure from "public/images/enerflo-residential-solar-finance-integrations.png";
import ProjectManagementFigure from "public/images/enerflo-residential-solar-install-tracker-project-management.png";
import ProposalsFigure from "public/images/enerflo-residential-solar-sales-proposals.png";
import IntegrationsFigure from "public/images/enerflo-residential-solar-tools-apps-integrations.png";
import VSRFigure from "public/images/enerflo-residential-solar-virtual-sales-rep-vsr.png";

import HeroSection from "components/hero-section";

import {
  FeatureBlockContainer,
  FeatureBlockFigure,
  FeatureBlockHeader,
  ListBlock,
  type FeatureBlockProps,
} from "./feature-block";
import { sectionIds } from "./section-ids";

const checkIfMobile = () => {
  if (typeof window === "undefined") {
    return false;
  }
  return document.documentElement.clientWidth < 640;
};

const ListItems = ({ items }: { items: string[] }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const syncMobile = () => {
      setIsMobile(checkIfMobile());
    };

    syncMobile();

    window.addEventListener("resize", syncMobile);

    return () => {
      window.removeEventListener("resize", syncMobile);
    };
  }, []);

  if (items.length > 6 && !isMobile) {
    const middleIndex = Math.ceil(items.length / 2);
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
        <ListBlock items={items.slice(0, middleIndex)} iconClassName="text-primary" />
        <ListBlock items={items.slice(middleIndex)} iconClassName="text-primary" />
      </div>
    );
  }

  return <ListBlock items={items} iconClassName="text-primary" />;
};

const FeatureBlock = ({ title, heading, description, items, action, image }: FeatureBlockProps) => {
  return (
    <FeatureBlockContainer>
      <FeatureBlockFigure className="xs:w-4/5 sm:w-3/4 md:w-full" {...image} />

      <FeatureBlockHeader title={title} titleClassName="text-primary">
        {heading}
      </FeatureBlockHeader>

      <div className="space-y-6">
        <p className="text-gray-600">{description}</p>
        <ListItems items={items} />
      </div>

      <HeroSection.Action className="btn-primary" href={action.href}>
        {action.label}
      </HeroSection.Action>
    </FeatureBlockContainer>
  );
};

const sections = [
  {
    id: sectionIds.integratedTools,
    blocks: [
      {
        title: "CRM, Solar Design, Solar Finance And More Integrations",
        heading: "Integrated Tools & Apps",
        description:
          "Bring your canvassing app, CRM, design tool, dialer. Enerflo connects them all via open APIs and 150+ native integrations.",
        items: [
          "All major CRMs",
          "High accuracy 3D solar design software integrations",
          "Financing integrations for loans, TPO (lease & PPA), escrow for cash",
          "Connect other tools with Enerflo’s open API",
        ],
        action: { href: "/integrations", label: "Explore Integrations" },
        imageSrc: IntegrationsFigure,
      },
      {
        title: "From First Knock To Project Hand-off, Everything Reps Need To Sell Solar",
        heading: "Solar Sales Process (Front End)",
        description:
          "Enerflo connects the entire solar deal flow—from first conversation to signed contract and PTO—in one operating system. Reps generate proposals with integrated design, pricing, financing, and documentation, so deals move faster and reach operations complete and compliant.",
        items: [
          "Leads & Deals Pipeline",
          "Scheduling",
          "QuickQuote",
          "Virtual Sales Rep (VSR)",
          "Solar Design & Proposal",
          "Battery Sizing Tool",
          "Title Checks, Soft Pulls",
          "Solar Financing (Loan, TPO, Escrow)",
          "Solar Contracting with Solar Signing Packets in Seconds",
          "Complete Project Handoff",
          "Project Status Transparency",
        ],
        action: { href: "/features/sales-process", label: "Explore the Solar Sales Process" },
        imageSrc: ProposalsFigure,
      },
    ],
  },

  {
    id: sectionIds.vsr,
    blocks: [
      {
        title: "Capture, Qualify & Deliver Residential Solar Leads 24/7",
        heading: "Virtual Sales Rep (VSR)",
        description:
          "Enerflo VSR is the newest innovation from Enerflo designed to help residential solar installers capture more qualified leads, accelerate speed-to-lead, and amplify the ROI of your paid and organic marketing efforts.",
        items: [
          "White Labeled, Web-Based",
          "Interactive Lead Capture",
          "Referral Code Setup",
          "Unlimited Configurations",
          "Instant Quote Generation",
          "Automated Lead Delivery",
        ],
        action: { href: "/features/virtual-sales-rep", label: "Explore Virtual Sales Rep (VSR)" },
        imageSrc: VSRFigure,
      },
      {
        title: "Fast, Accurate 3d Solar Design Software",
        heading: "Integrated Solar Design",
        description:
          "Bring system design into the solar sales process with Enerflo’s integrated design software. Reps can create complete, customer-ready proposals without switching tools, connecting design, pricing, and financing in one workflow to reduce delays and improve close rates.",
        items: ["Aurora Solar", "Scanifly", "Aerialytic", "Solargraf", "Solo", "Manual Designs"],
        action: { href: "/integrations/solar-design", label: "Explore Solar Design Integrations" },
        imageSrc: IntegratedDesignsFigure,
      },
    ],
  },
  {
    id: sectionIds.financing,
    blocks: [
      {
        title: "Solar Finance Integrations For Loans, Tpo (Lease & Ppa), Escrow For Cash",
        heading: "Integrated Solar Financing",
        description:
          "Connect solar financing into your sales workflow with Enerflo’s Lendflo financing system. Reps present real-time loan and lease options while deals are routed across lenders to increase approvals and close faster.",
        items: [
          "Competitive Loan Products (Escalators, Low/No Dealer Fees)",
          "Third Party Owned (TPO) - Lease & PPA for Solar & Storage",
          "Third Party Owned (TPO) - Battery Backup",
          "Escrow for Cash Deals",
        ],
        action: { href: "/features/lendflo", label: "Explore Solar Financing Integrations" },
        imageSrc: FinanceIntegrationsFigure,
      },
      {
        title: "Complete, Dynamic Solar Signing Packets In Seconds",
        heading: "Solar Contracting",
        description:
          "Generate complete, compliant solar contract packages in seconds with Enerflo’s Docflo contracting system. Automatically compile all required documents into one signing packet and execute with fast e-signatures, reducing paperwork and speeding deals to install.",
        items: [
          "Complete Solar Signing Packets in Seconds",
          "Fast, Easy, Accurate, Secure",
          "Send for Signature via Email",
          "In-Person Electronic Signature",
        ],
        action: { href: "/features/docflo", label: "Explore Solar Contracting" },
        imageSrc: ContractingFigure,
      },
    ],
  },
  {
    id: sectionIds.customerPortals,
    blocks: [
      {
        title: "Dedicated Project Portal For Every Customer",
        heading: "Solar Customer Portals",
        description:
          "Keep homeowners engaged from first conversation to installation with Enerflo’s solar customer portal. Centralize project details, documents, and real-time status in one place, while branded content and referrals help build trust and increase close rates.",
        items: [
          "Dedicated Portal for Every Customer (No login required)",
          "Install Tracker with Real-Time Status",
          "Centralize Solar Project Details & Documentation",
          "Centralize Communications",
          "Pre and Post Contract Visibility",
        ],
        action: { href: "/features/customer-engagement", label: "Explore Customer Portals" },
        imageSrc: CustomerPortalFigure,
      },
      {
        title: "From Solar Project Intake Through Permission To Operate (PTO)",
        heading: "Solar Install Tracker (Backend)",
        description:
          "Manage solar projects from contract to PTO with Enerflo’s Install Tracker. Install Tracker includes configurable milestones, task management, and automated communications to help keep your projects on track and moving forward.",
        items: [
          "Install Tracker with Configurable Milestones",
          "Project & Task Management",
          "Automated Customer Communications",
          "Accuracy on the Frontend Prevents Changes on the Backend",
          "Integrated Change Orders",
        ],
        action: { href: "/features/project-management", label: "Explore Project Management" },
        imageSrc: ProjectManagementFigure,
      },
    ],
  },
  {
    id: sectionIds.reporting,
    blocks: [
      {
        title: "Actionable Insights & Business Intelligence Powered By Domo",
        heading: "Reporting & Analytics for Solar Companies",
        description:
          "Make data-driven decisions with Enerflo’s solar reporting and analytics, powered by Domo. Access hundreds of reports on sales, pipeline, and installs—giving you the visibility to compete and optimize without building your own BI stack.",
        items: [
          "24/7/365 Access to Centralized Data",
          "Visual Dashboards",
          "Filters & Annotations",
          "Share, Export & Schedule Reports",
        ],
        action: { href: "/features/reporting", label: "Explore Reporting & Analytics" },
        imageSrc: ReportingFigure,
      },
      {
        title: "Customer Service Is A Priority, Not An Afterthought",
        heading: "Customer Support that Keeps Projects Moving",
        description:
          "As the operating system for residential solar, Enerflo supports residential solar companies of all types and sizes in all 50 states, and Puerto Rico. With fast response times, high resolution rates and a 97% customer satisfaction rating, the Enerflo Support team keeps reps and projects moving forward smoothly.",
        items: [
          "Chat with Enerflo Support from inside Enerflo during support hours",
          "If Enerflo’s AI assistant Flo can’t help, a human is standing by",
          "Response times in 1 minute or less",
          "97% customer satisfaction rate over tens of thousands of chats",
        ],
        action: { href: "/about/contact", label: "Contact Enerflo" },
        imageSrc: CustomerSupportFigure,
      },
    ],
  },
];

const FeatureSections = () => {
  return (
    <>
      {sections.map(section => (
        <div className="bg-gradient-to-b from-white to-gray-100" key={section.id}>
          <HeroSection title={section.blocks[0].title} id={section.id}>
            <HeroSection.Content className="grid grid-cols-1 md:grid-cols-2 gap-16">
              {section.blocks.map(({ imageSrc, ...block }) => (
                <FeatureBlock key={block.title} {...block} image={{ src: imageSrc, alt: block.heading }} />
              ))}
            </HeroSection.Content>
          </HeroSection>
        </div>
      ))}
    </>
  );
};

export default FeatureSections;
